import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ACCESSED } from '../data/sources';
import { getSourceById, hostOf, lookupByUrl } from '../utils/sourceIndex';
interface SourceRefProps {
  id?: string;
  href?: string;
  label?: string;
  estimate?: {
    low: string;
    high: string;
  };
  note?: string;
}
interface TipPos {
  x: number;
  y: number;
  below: boolean;
}
const markerClass = 'ml-[2px] inline-block rounded-[3px] px-[3px] font-mono text-[0.62rem] font-medium leading-none text-machine/90 align-super no-underline transition-colors duration-150 ease-out hover:bg-machine/15 hover:text-machine focus-visible:bg-machine/15';
export function SourceRef({
  id,
  href,
  label,
  estimate,
  note
}: SourceRefProps) {
  const ref = useRef<HTMLAnchorElement>(null);
  const [tip, setTip] = useState<TipPos | null>(null);
  useEffect(() => {
    if (!tip) return;
    const close = () => setTip(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [tip]);
  const indexed = id ? getSourceById(id) : href ? lookupByUrl(href) : undefined;
  // When both id and href are given, keep the registry number but link to the specific (e.g. per-country) URL.
  const url = id && href ? href : indexed?.url ?? href;
  if (!url) return null;
  const name = indexed?.name ?? label ?? hostOf(url);
  const publisher = indexed?.publisher ?? hostOf(url);
  const marker = indexed ? String(indexed.n) : '↗';
  const internal = url.startsWith('/');
  const aria = `Source ${marker}: ${name}${estimate ? `. Estimate, range ${estimate.low} to ${estimate.high}` : ''}${internal ? '' : '. Opens in new tab'}`;
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const below = r.top < 160;
    const x = Math.min(window.innerWidth - 152, Math.max(152, r.left + r.width / 2));
    setTip({
      x,
      y: below ? r.bottom + 8 : r.top - 8,
      below
    });
  };
  const hide = () => setTip(null);
  const handlers = {
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide
  };
  return <sup className="leading-none" data-id="element-507" data-mp="34e3m0pk6t8mk">
      {internal ? <Link ref={ref} to={url} className={markerClass} aria-label={aria} {...handlers} data-id="element-508" data-mp="34cis925p5tpr">
          {marker}
        </Link> : <a ref={ref} href={url} target="_blank" rel="noopener noreferrer" className={markerClass} aria-label={aria} {...handlers} data-id="element-509" data-mp="3des0tmhend0i">
          {marker}
        </a>}
      {tip && createPortal(<div role="tooltip" className="census-tip pointer-events-none fixed z-[90] w-[288px] rounded-md border border-line bg-raised px-3 py-2.5 text-left shadow-2xl shadow-black/60" style={{
      left: tip.x,
      top: tip.y,
      transform: `translate(-50%, ${tip.below ? '0' : '-100%'})`
    }} data-id="element-510" data-mp="3n4v3keebw5c1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted" data-id="element-511" data-mp="jmqii3kw3lgb">
              [{marker}] {publisher}
            </p>
            <p className="mt-1 text-[13px] leading-snug text-ink" data-id="element-512" data-mp="oinyg8s8qpsk">{name}</p>
            {estimate && <p className="mt-1.5 font-mono text-[11px] text-machine" data-id="element-513" data-mp="an3jfofmurqf">
                Estimate · range {estimate.low}–{estimate.high}
              </p>}
            {note && <p className="mt-1 text-[12px] text-muted" data-id="element-514" data-mp="tmogh3rf6l6">{note}</p>}
            <p className="mt-1.5 font-mono text-[10px] text-faint" data-id="element-515" data-mp="5m5yx575hllr">{internal ? 'Agent Census methodology' : `Accessed ${ACCESSED} · opens in new tab`}</p>
          </div>, document.body)}
    </sup>;
}